'use client';

import React, { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, InputRef } from 'antd';

import { Input, YandexCaptcha, GoogleCaptcha, CaptchaErrorAlert } from 'components';

import { FormButtons } from 'modules';

import useForm from 'hooks/forms/useForm';
import useValidation from 'hooks/forms/useValidation';
import useAutoFocus from 'hooks/elements/useAutoFocus';
import useInitialData from 'hooks/common/useInitialData';

import { CAPTCHA_TYPE } from 'config';
import { ErrorType } from 'constants/errors';
import { Y_CAPTCHA_PARAM_KEY } from 'constants/captcha/yandex';

import { CaptchaType } from 'types/captcha';
import { InputVariant } from 'components/inputs/Input';
import { IAuthenticationFormProps } from './AuthenticationForm.types';

import './AuthenticationForm.styles.scss';

const AuthenticationForm = ({
  loading,
  showGoBack,
  includeEmail,
  includePassword,
  includeGa,
  includeCaptcha,
  disabled,
  error,
  yandexCaptcha,
  googleCaptcha,
  onFinish,
  onGoBack,
  onSubmitCaptcha,
  className = '',
}: IAuthenticationFormProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const rules = useValidation();

  const { submitDisabled } = useForm(form);

  const totpRef = useRef<InputRef | null>(null);
  const emailRef = useRef<InputRef | null>(null);
  const passwordRef = useRef<InputRef | null>(null);

  const autoFocusRef = useMemo(() => {
    if (includeEmail) {
      return emailRef;
    }

    if (includePassword) {
      return passwordRef;
    }

    if (includeGa) {
      return totpRef;
    }
  }, [includeEmail, includeGa, includePassword]);

  useAutoFocus(autoFocusRef);

  const resetGoogleCaptchaField = () => {
    form.setFieldValue('captchaV2', null);
  };

  useInitialData(googleCaptcha?.key, resetGoogleCaptchaField);

  const showGoogleCaptcha = CAPTCHA_TYPE === CaptchaType.GOOGLE && includeCaptcha && googleCaptcha?.isCaptchaError;
  const showYandexCaptcha = CAPTCHA_TYPE === CaptchaType.YANDEX && includeCaptcha;

  const isYandexCaptchaError = yandexCaptcha?.checkIsCaptchaError?.(error);
  const isGoogleCaptchaError = error === ErrorType.CAPTCHA_FINAL_ERROR;

  const showCaptchaErrorAlert = isYandexCaptchaError || isGoogleCaptchaError;

  const isSubmitButtonDisabled = useMemo(() => {
    if (!includeCaptcha) {
      return submitDisabled || disabled;
    }

    switch (CAPTCHA_TYPE) {
      case CaptchaType.YANDEX:
        return submitDisabled || disabled || isYandexCaptchaError;

      case CaptchaType.GOOGLE:
      default:
        return submitDisabled || disabled || !googleCaptcha?.isCaptchaReady;
    }
  }, [includeCaptcha, submitDisabled, disabled, isYandexCaptchaError, googleCaptcha?.isCaptchaReady]);

  return (
    <div className="np-authentication-form__wrapper">
      <Form
        requiredMark="optional"
        layout="vertical"
        form={form}
        name="auth"
        onFinish={onFinish}
        className={`np-authentication-form ${className}`}
      >
        {includeEmail && (
          <Form.Item validateFirst name="email" label={t('form.label.email')} rules={[rules.required, rules.email]}>
            <Input autoFocus ref={emailRef} size="large" variant={InputVariant.BORDERED} disabled={loading} />
          </Form.Item>
        )}

        {includePassword && (
          <Form.Item
            name="password"
            validateFirst
            label={t('form.label.password')}
            rules={[rules.required]}
            className="np-authentication-form__input-block"
          >
            <Input.Password ref={passwordRef} size="large" variant={InputVariant.BORDERED} disabled={loading} />
          </Form.Item>
        )}

        {includeGa && (
          <Form.Item
            name="totp"
            validateFirst
            label={t('form.label.gaCode')}
            rules={rules.ga}
            className="np-authentication-form__input-block"
          >
            <Input ref={totpRef} size="large" variant={InputVariant.BORDERED} disabled={loading} />
          </Form.Item>
        )}

        {showGoogleCaptcha && (
          <Form.Item name="captchaV2" validateFirst rules={[rules.required]}>
            <GoogleCaptcha key={googleCaptcha.key} />
          </Form.Item>
        )}

        {showYandexCaptcha && (
          <YandexCaptcha
            key={yandexCaptcha.key}
            visible={yandexCaptcha.visible}
            onChange={yandexCaptcha.onChange}
            onSuccess={token => onSubmitCaptcha({ ...form.getFieldsValue(), [Y_CAPTCHA_PARAM_KEY]: token })}
            onChangeStatus={yandexCaptcha.onChangeStatus}
            onChangeVisible={yandexCaptcha.onChangeVisible}
          />
        )}

        {showCaptchaErrorAlert && <CaptchaErrorAlert type={error} />}

        <FormButtons
          showGoBack={showGoBack}
          submitDisabled={isSubmitButtonDisabled}
          loading={loading}
          marginTop={12}
          onGoBack={onGoBack}
        />
      </Form>
    </div>
  );
};

export default AuthenticationForm;
